import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { getLandingUrlWithSettings } from '@/utils/urls'

/**
 * Composable for landing page link visibility and URL building
 * Combines feature flag checking with landing URL generation
 *
 * Usage:
 * ```ts
 * const { isLandingEnabled, landingUrl } = useLandingPageLinks()
 * // Use v-if="isLandingEnabled" to conditionally show links
 * // Use :href="landingUrl" when landingUrl is not null
 * ```
 */
export function useLandingPageLinks() {
  const { isEnabled, loading, error } = useFeatureFlag()

  const isLandingEnabled: ComputedRef<boolean> = isEnabled('landing_enabled')

  const landingUrl: ComputedRef<string | null> = computed(() =>
    isLandingEnabled.value ? getLandingUrlWithSettings() : null
  )

  /**
   * Get landing URL with custom path
   * @param path - Path on the landing page (e.g., '/pricing', '/features')
   * @returns URL string if landing enabled, null otherwise
   */
  function getLandingUrl(path = '/'): ComputedRef<string | null> {
    return computed(() =>
      isLandingEnabled.value ? getLandingUrlWithSettings(path) : null
    )
  }

  return {
    isLandingEnabled,
    landingUrl,
    getLandingUrl,
    loading,
    error,
  }
}
